import React from 'react'
import { motion } from 'framer-motion'
import { useTranslation } from 'react-i18next'

const PrivacyPolicy = () => {
  const { t } = useTranslation();
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 pt-20">
      <div className="container mx-auto px-4 py-12 max-w-4xl">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="bg-white/5 backdrop-blur-lg rounded-3xl p-8 border border-white/10"
        >
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-4xl font-bold text-white mb-8 text-center"
          >
            {t('legal.privacyPolicy.title')}
          </motion.h1>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="prose prose-invert max-w-none text-gray-300 space-y-6"
          >
            <div className="mb-6 p-4 bg-emerald-500/10 rounded-lg border border-emerald-500/20">
              <p className="text-emerald-300 font-medium">
                Konvo AI ("we," "us," "our") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our AI Voice Assistant service and website www.konvoai.tech (collectively, the "Service").
              </p>
              <p className="mt-3 text-gray-300">
                By using our Service, you consent to the data practices described in this policy.
              </p>
            </div>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">1. Information We Collect</h2>
              <p className="mb-4">We collect several types of information to provide and improve our Service.</p>

              <div className="space-y-4">
                <div>
                  <h3 className="text-lg font-medium text-emerald-300 mb-2">a. Personal Information:</h3>
                  <p>When you register for an account, we may collect identifiable information such as your name, email address, and contact details.</p>
                </div>

                <div>
                  <h3 className="text-lg font-medium text-emerald-300 mb-2">b. Voice Data:</h3>
                  <p>This is the core of our Service. We collect and process your voice inputs and commands. This data may include the audio recording of your voice and the textual transcript of your commands.</p>
                </div>

                <div>
                  <h3 className="text-lg font-medium text-emerald-300 mb-2">c. Usage Data:</h3>
                  <p>We automatically collect information on how you interact with the Service. This may include your IP address, device type, operating system, unique device identifiers, browser type, pages visited, the time and date of your visits, and other diagnostic data.</p>
                </div>

                <div>
                  <h3 className="text-lg font-medium text-emerald-300 mb-2">d. Cookies and Tracking Technologies:</h3>
                  <p>We use cookies and similar tracking technologies to track activity on our Service and hold certain information. Cookies are files with a small amount of data which may include an anonymous unique identifier.</p>
                </div>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">2. How We Use Your Information</h2>
              <p className="mb-4">We use the collected information for various purposes:</p>

              <div className="space-y-4">
                <div>
                  <h3 className="text-lg font-medium text-emerald-300 mb-2">a. To Provide and Maintain the Service:</h3>
                  <p>To operate, maintain, and provide you with the features and functionality of the Konvo AI assistant.</p>
                </div>

                <div>
                  <h3 className="text-lg font-medium text-emerald-300 mb-2">b. To Improve and Train our AI Models:</h3>
                  <p>Your voice inputs and interactions are used to train, refine, and improve the accuracy and performance of our artificial intelligence algorithms. This is essential for the Service to learn and become better.</p>
                </div>

                <div>
                  <h3 className="text-lg font-medium text-emerald-300 mb-2">c. To Communicate with You:</h3>
                  <p>To send you service-related notices, technical updates, security alerts, and support messages.</p>
                </div>

                <div>
                  <h3 className="text-lg font-medium text-emerald-300 mb-2">d. For Customer Support:</h3>
                  <p>To respond to your comments and questions and provide user support.</p>
                </div>

                <div>
                  <h3 className="text-lg font-medium text-emerald-300 mb-2">e. For Analytics:</h3>
                  <p>To understand how our Service is used, monitor usage patterns, and improve user experience.</p>
                </div>

                <div>
                  <h3 className="text-lg font-medium text-emerald-300 mb-2">f. To Ensure Security:</h3>
                  <p>To detect, prevent, and address technical issues, fraud, and potential security breaches.</p>
                </div>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">3. Legal Basis for Processing (Under GDPR/DPDPA)</h2>
              <div className="space-y-4">
                <div>
                  <h3 className="text-lg font-medium text-emerald-300 mb-2">For users in the European Union:</h3>
                  <p className="mb-2">Our legal basis for processing your data is primarily:</p>
                  <ul className="list-disc pl-6 space-y-1">
                    <li><strong>Performance of a Contract:</strong> Processing is necessary to provide the Service under our Terms of Service.</li>
                    <li><strong>Consent:</strong> For certain non-essential purposes, we will rely on your consent.</li>
                    <li><strong>Legitimate Interests:</strong> We process data to improve our Service and for security purposes, which are our legitimate interests.</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-lg font-medium text-emerald-300 mb-2">For users in India:</h3>
                  <p>We process your data in compliance with the Digital Personal Data Protection Act, 2023, which may require us to rely on consent or legitimate uses as defined under the law.</p>
                </div>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">4. Sharing and Disclosure of Information</h2>
              <p className="mb-4">We do not sell your personal information. We may share your information in the following situations:</p>

              <div className="space-y-4">
                <div>
                  <h3 className="text-lg font-medium text-emerald-300 mb-2">a. With Service Providers:</h3>
                  <p>We may share your information with third-party vendors who perform services on our behalf, such as cloud hosting, data analysis, payment processing, and customer service. These providers are contractually bound to protect your data.</p>
                </div>

                <div>
                  <h3 className="text-lg font-medium text-emerald-300 mb-2">b. For Legal Reasons:</h3>
                  <p>We may disclose your information if required to do so by law or in response to valid requests by public authorities (e.g., a court or a government agency).</p>
                </div>

                <div>
                  <h3 className="text-lg font-medium text-emerald-300 mb-2">c. To Protect Rights:</h3>
                  <p>If we believe your actions are inconsistent with our user agreements or policies, or to protect the rights, property, and safety of Konvo AI or others.</p>
                </div>

                <div>
                  <h3 className="text-lg font-medium text-emerald-300 mb-2">d. Business Transfers:</h3>
                  <p>In connection with, or during negotiations of, any merger, sale of company assets, financing, or acquisition of all or a portion of our business by another company.</p>
                </div>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">5. Data Retention</h2>
              <p>
                We will retain your Personal Information only for as long as is necessary for the purposes set out in this Privacy Policy, and to the extent necessary to comply with our legal obligations, resolve disputes, and enforce our policies. Usage data is generally retained for a shorter period, except when this data is used to strengthen the security or to improve the functionality of our Service, or we are legally obligated to retain this data for longer time periods.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">6. Data Security</h2>
              <p>
                We implement appropriate technical and organizational security measures designed to protect your data from unauthorized access, alteration, disclosure, or destruction. These include encryption of data in transit and at rest, access controls, and secure server infrastructure. However, no method of transmission over the Internet or electronic storage is 100% secure, and we cannot guarantee its absolute security.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">7. Your Data Protection Rights</h2>
              <p className="mb-4">Depending on your location, you may have the following rights regarding your personal data:</p>

              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Access and Portability:</strong> The right to access and receive a copy of your personal data.</li>
                <li><strong>Rectification:</strong> The right to correct any inaccurate or incomplete personal data.</li>
                <li><strong>Erasure ("Right to be Forgotten"):</strong> The right to request the deletion of your personal data.</li>
                <li><strong>Restriction of Processing:</strong> The right to request that we restrict the processing of your personal data.</li>
                <li><strong>Objection to Processing:</strong> The right to object to our processing of your personal data.</li>
                <li><strong>Withdraw Consent:</strong> Where we rely on consent, you have the right to withdraw it at any time.</li>
              </ul>

              <p className="mt-4">
                To exercise any of these rights, please contact us using the details provided below. We will respond to your request in accordance with applicable law.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">8. International Data Transfers</h2>
              <p>
                Your information, including Personal Data, may be transferred to — and maintained on — computers located outside of your state, province, country, or other governmental jurisdiction where the data protection laws may differ. We will take all steps reasonably necessary to ensure that your data is treated securely and in accordance with this Privacy Policy.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">9. Children's Privacy</h2>
              <p>
                Our Service is not directed to anyone under the age of 13 ("Children"). We do not knowingly collect personally identifiable information from children under 13. If you are a parent or guardian and you are aware that your child has provided us with Personal Data, please contact us. If we become aware that we have collected Personal Data from children without verification of parental consent, we take steps to remove that information from our servers.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">10. Changes to This Privacy Policy</h2>
              <p>
                We may update our Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last Updated" date. You are advised to review this Privacy Policy periodically for any changes.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">11. Grievance Officer</h2>
              <p className="mb-4">
                In compliance with Indian IT laws, we have appointed a Grievance Officer. If you have any questions or grievances about this Privacy Policy or our data practices, you can contact the officer at:
              </p>
              <div className="mt-4 p-4 bg-white/5 rounded-lg border border-white/10">
                <p className="font-medium text-emerald-300">Name: Srinivas Raju</p>
                <p>Address: 7th floor, T-hub, Raidurgam panmaktha Hyderabad Knowledge City, Serilingampally, Hyderabad, Telangana 500081</p>
                <p>Email: info@konvoai.tech</p>
              </div>
              <p className="mt-4">
                We will address your concern within the timeframe prescribed by applicable law.
              </p>
            </section>


            <div className="mt-8 pt-6 border-t border-white/20">
              <p className="text-sm text-gray-400 text-center">
                Last updated: October 2025
              </p>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </div>
  )
}

export default PrivacyPolicy